import { useScreenSize } from '@Hooks/useScreenSize';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React from 'react';
import { Platform, StyleSheet } from 'react-native';
import { Snackbar } from 'react-native-paper';
import styled from 'styled-components/native';

import OwnableTheme from '../../assets/OwnableTheme';

import toastStore from '~stores/toast';
import type { IMessage, ToastType } from '~stores/toast';
import { ScreenSize } from '~types/views-types';

type ISnackbarProps = {
  duration?: number;
};

type IStyledMessageTextProp = {
  type: ToastType;
};

const StyledSnackbarContainer = styled.View`
  position: absolute;
  flex: 1;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  width: 100%;
  z-index: 99999999999;
`;

const styles = StyleSheet.create({
  container: {
    marginBottom: 25,
    maxWidth: 400,
  },
  containerMobile: {
    marginBottom: Platform.OS === 'web' ? 100 : 80,
    maxWidth: 400,
  },
  infoToast: {
    borderRadius: 32,
    backgroundColor: OwnableTheme.colors.white,
  },
  errorToast: {
    borderRadius: 32,
    backgroundColor: OwnableTheme.colors.amaranth,
  },
});

const infoToastTheme = { colors: { accent: OwnableTheme.colors.amaranth } };
const errorToastTheme = { colors: { accent: OwnableTheme.colors.white } };

const StyledMessageText = styled.Text`
  font-family: ${OwnableTheme.fonts.gilroy.bold};
  color: ${(props: IStyledMessageTextProp) =>
    props.type === 'error'
      ? OwnableTheme.colors.white
      : OwnableTheme.colors.persianIndigo};
`;

export const Toast: FC<ISnackbarProps> = observer(({ duration = 7000 }) => {
  const { queue, removeMessage } = toastStore;

  const [size] = useScreenSize();

  const snackBars = queue.map((message: IMessage) => (
    <Snackbar
      key={message?.key}
      duration={message?.duration ?? duration}
      visible={Boolean(message)}
      onDismiss={() => {
        removeMessage(message.key);
      }}
      style={message.type === 'error' ? styles.errorToast : styles.infoToast}
      wrapperStyle={
        [
          ScreenSize.mobile,
          ScreenSize.thinmobile,
          ScreenSize.tabletPortrait,
        ].includes(size)
          ? styles.containerMobile
          : styles.container
      }
      action={{
        label: 'X',
        onPress: () => {
          removeMessage(message.key);
        },
      }}
      theme={message.type === 'error' ? errorToastTheme : infoToastTheme}
    >
      <StyledMessageText type={message.type}>
        {message.message}
      </StyledMessageText>
    </Snackbar>
  ));

  return (
    <StyledSnackbarContainer pointerEvents='none'>
      {snackBars}
    </StyledSnackbarContainer>
  );
});
